import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

import {
  Device,
  DeviceRequest,
  DeviceUpdateStatusRequest,
  ValidateImportDevice,
  ImportDeviceData,
  DeviceDownloadResponse,
  DeviceImportRequest,
  DeviceImportResponse,
  DeviceCancelResponse,
  DeviceStatusDetail,
  DeviceSearchRequest,
  DeviceSearchResponse,
  DeviceTagResponse,
  DeviceDataModel,
  GetDevicesByLocationsModel,
  DeviceChartData,
  DeviceUsageChartDataModel,
  EGaugeChartTableViewModel,
  EGaugeChartTableViewDataModel,
  ChartTableViewModel,
  ChartTableViewDataModel,
  EGaugeChartModel,
  EGaugeChartDataModel,
  eGaugeChartModelData,
  ChartDataModel,
  DeviceChartPayload
} from '../models/device.model';
import { DeviceType } from '../models/devicetype.model';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  private apiUrl = `${environment.apiURL}`;

  constructor(private http: HttpClient) { }

  get baseApiUrl(): string {
    return this.apiUrl;
  }

  // Get all devices
  getDevices(): Observable<Device[]> {
    return this.http.get<Device[]>(`${this.apiUrl}/device/list`);
  }

  // Get device by ID
  getDeviceById(deviceId: string, isDeviceAttributeRequried: boolean): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/device/details/${deviceId}?isDeviceAttributeRequried=${isDeviceAttributeRequried}`);
  }

  // Get all devices by locationId
  getDevicesByLocations(locationIds: string[], deviceTemplateId: string): Observable<DeviceDataModel[]> {
    const payload: GetDevicesByLocationsModel = { locationIds, deviceTemplateId };
    return this.http.post<DeviceDataModel[]>(`${this.apiUrl}/device/devices`, payload);
  }

  // Create new device
  createDevice(deviceRequest: DeviceRequest): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/device/add`, deviceRequest);
  }

  // Update existing device
  updateDevice(deviceId: string, deviceRequest: DeviceRequest): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/device/edit/${deviceId}`, deviceRequest);
  }

  // Delete device
  deleteDevice(iotDeviceId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/device/delete/${iotDeviceId}`);
  }

  // Update device status (Active/Inactive)
  updateDeviceStatus(deviceId: string, statusRequest: DeviceUpdateStatusRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/device/status/${deviceId}`, statusRequest);
  }

  // Upload device template file
  uploadDeviceFile(formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/upload`, formData);
  }

  // Download device template
  downloadDeviceTemplate(deviceId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/device/download/${deviceId}`, {
      responseType: 'blob'
    });
  }

  // Validate import device
  validateImportDevice(locationId: string, deviceTemplateId: string, file: File, deviceTypeId: string, isAutoGeneratedCertificate: boolean, certificateAuthorityId: string | null): Observable<ValidateImportDevice> {
    const formData = new FormData();
    formData.append('entityGuid', locationId);
    formData.append('deviceTemplateGuid', deviceTemplateId);
    formData.append('formFile', file);
    formData.append('deviceTypeId', deviceTypeId);
    formData.append('isAutoGeneratedCertificate', String(isAutoGeneratedCertificate));
    if (!isAutoGeneratedCertificate && certificateAuthorityId) {
      formData.append('CertificateAuthorityGuid', certificateAuthorityId);
    }

    return this.http.post<ValidateImportDevice>(`${this.apiUrl}/device/validate`, formData);
  }

  // Get parent device list for a given entity and device template
  getParentDeviceList(entityGuid: string, deviceTemplateGuid: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/device/parent-device-list/${entityGuid}/${deviceTemplateGuid}`);
  }

  // Import child device
  importChildDevice(deviceUniqueId: string, entityGuid: string, deviceTemplateGuid: string, deviceTypeId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('DeviceUniqueId', deviceUniqueId);
    formData.append('EntityGuid', entityGuid);
    formData.append('DeviceTemplateGuid', deviceTemplateGuid);
    formData.append('DeviceTypeId', deviceTypeId);
    formData.append('FormFile', file);

    return this.http.post<any>(`${this.apiUrl}/device/import-child-device`, formData);
  }

  // Get import device list
  getImportDeviceList(locationId: string, deviceTemplateId: string): Observable<ImportDeviceData[]> {
    return this.http.get<ImportDeviceData[]>(`${this.apiUrl}/device/getImportDeviceList/${locationId}/${deviceTemplateId}`);
  }

  // Download device import file by guid
  downloadDeviceImport(guid: string): Observable<DeviceDownloadResponse[]> {
    return this.http.get<DeviceDownloadResponse[]>(`${this.apiUrl}/device/download/${guid}`);
  }

  // Import device by guid
  importDevice(importDeviceGuid: string): Observable<DeviceImportResponse> {
    const importRequest: DeviceImportRequest = {
      importDeviceGuid: importDeviceGuid
    };
    return this.http.put<DeviceImportResponse>(`${this.apiUrl}/device/import`, importRequest);
  }

  // Cancel device import by guid
  cancelDeviceImport(guid: string): Observable<DeviceCancelResponse> {
    return this.http.delete<DeviceCancelResponse>(`${this.apiUrl}/device/cancel/${guid}`);
  }

  // Get success device details by guid
  getSuccessDeviceDetails(guid: string): Observable<DeviceStatusDetail[]> {
    return this.http.get<DeviceStatusDetail[]>(`${this.apiUrl}/device/success/${guid}`);
  }

  // Get error device details by guid
  getErrorDeviceDetails(guid: string): Observable<DeviceStatusDetail[]> {
    return this.http.get<DeviceStatusDetail[]>(`${this.apiUrl}/device/error/${guid}`);
  }

  getDeviceTypes(): Observable<DeviceType[]> {
    return this.http.get<DeviceType[]>(`${this.apiUrl}/devicetype/list`);
  }

  getCertificateAuthorities(jitSupport?: boolean): Observable<any> {
    let url = `${this.apiUrl}/device/certificate/lookup/2`;
    if (jitSupport !== undefined) {
      url += `?jitSupport=${jitSupport}`;
    }
    return this.http.get<any>(url);
  }

  // Search devices with pagination and sorting
  searchDevices(searchRequest: DeviceSearchRequest): Observable<DeviceSearchResponse> {
    return this.http.post<DeviceSearchResponse>(`${this.apiUrl}/device/search`, searchRequest);
  }

  // Get device tags by device ID
  getDeviceTagsByDeviceId(deviceId: string): Observable<DeviceTagResponse[]> {
    return this.http.get<DeviceTagResponse[]>(`${this.apiUrl}/device/tags/${deviceId}`);
  }

  // Get client tags by client ID (for device tag assignment)
  getClientTagsByClientId(clientId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/clienttag/list/${clientId}`);
  }

  // Assign tags to device
  assignTagsByDeviceId(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/assignTagsByDeviceId`, request);
  }

  // Remove tag from device
  removeTagFromDevice(deviceId: string, tagId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/device/${deviceId}/removeTag/${tagId}`);
  }

  // Get devices by client ID
  getDevicesByClientId(clientId: string): Observable<Device[]> {
    return this.http.get<Device[]>(`${this.apiUrl}/device/by-client/${clientId}`);
  }

  // Get devices by tag ID
  getDevicesByTagId(tagId: number): Observable<Device[]> {
    return this.http.get<Device[]>(`${this.apiUrl}/device/by-tag/${tagId}`);
  }

  // Assign devices to tag
  assignDevicesToTag(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/assignDevicesToTag`, request);
  }

  // Remove device from tag
  removeDeviceFromTag(tagId: number, deviceId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/device/tag/${tagId}/device/${deviceId}`);
  }


  // Get device assignment hierarchy
  getDeviceHierarchy(iotDeviceId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/device/hierarchy/${iotDeviceId}`);
  }

  // Re-assign device to department
  reAssignDevice(iotDeviceId: string, departmentId: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/device/${iotDeviceId}/reAssign/${departmentId}`, {});
  }

  // Resync child devices of a gateway device (true = sync initiated, false = already up to date)
  resyncChildDevices(deviceId: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/device/resync-child-devices`, { deviceId });
  }

  getDevicesByDepartmentIds(departmentIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/device/departments/devices`, departmentIds);
  }

  getDevicesForCommands(departmentIds: string[], deviceTypeId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/departments/devices-commands`, { departmentIds, deviceTypeId });
  }

  getZonesForDevices(deviceIds: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/lighting/zones`, { deviceIds });
  }

  getDevicesTele(departmentIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/device/departments/devices`, departmentIds);
  }

  // Get device chart data
  getDeviceChartData(deviceId: string, startDate: Date, endDate: Date, deviceTypeName?: string): Observable<DeviceChartData> {
    const payload: DeviceChartPayload = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    if (deviceTypeName) {
      payload.deviceTypeName = deviceTypeName;
    }

    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<DeviceChartData>(
      `${this.apiUrl}/device/chartData`,
      payload,
      { headers }
    );
  }

  // Get eGauge usage chart data
  geteGaugeDeviceUsageChartData(deviceId: string, startDate: Date, endDate: Date, view: string = 'day', attributes: string[] = []): Observable<DeviceUsageChartDataModel> {
    const payload: EGaugeChartModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      view: view,
      attributes: attributes
    };
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<DeviceUsageChartDataModel>(`${this.apiUrl}/egaugedevice/geteGaugeUsageChart`, payload, { headers });
  }

  getEnergyChartData(deviceId: string, startDate: Date, endDate: Date, endPoint: string): Observable<ChartDataModel[]> {
    const payload: eGaugeChartModelData = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<ChartDataModel[]>(`${this.apiUrl}/egaugedevice/${endPoint}`, payload, { headers });
  }

  // Get eGauge usage chart table view data
  geteGaugeDeviceUsageChartTableViewData(deviceId: string, startDate: Date, endDate: Date, offset: number, limit: number, view: string = 'day', attributes: string[] = []): Observable<EGaugeChartTableViewDataModel> {
    const payload: EGaugeChartTableViewModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      offset: offset,
      limit: limit,
      view: view,
      attributes: attributes
    };
    return this.http.post<EGaugeChartTableViewDataModel>(`${this.apiUrl}/egaugedevice/geteGaugeUsageChartTableView`, payload);
  }

  // Get eGauge power chart data
  geteGaugeDevicePowerChartData(deviceId: string, startDate: Date, endDate: Date, view: string = 'day', attributes: string[] = []): Observable<ChartTableViewDataModel> {
    const payload: EGaugeChartModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      view: view,
      attributes: attributes
    };
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<ChartTableViewDataModel>(`${this.apiUrl}/egaugedevice/geteGaugePowerChart`, payload, { headers });
  }

  // Get eGauge power chart table view data
  geteGaugeDevicePowerChartTableViewData(deviceId: string, startDate: Date, endDate: Date, offset: number, limit: number, view: string = 'day', attributes: string[] = []): Observable<ChartTableViewDataModel> {
    const payload: EGaugeChartTableViewModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      offset: offset,
      limit: limit,
      view: view,
      attributes: attributes
    };
    return this.http.post<ChartTableViewDataModel>(`${this.apiUrl}/egaugedevice/geteGaugePowerChartTableView`, payload);
  }

  // Get power chart table view data
  getChartTableViewData(deviceId: string, startDate: Date, endDate: Date, offset: number, limit: number, view: string = 'day', deviceTypeName: string): Observable<ChartTableViewDataModel> {
    const payload: ChartTableViewModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      offset: offset,
      limit: limit,
      view: view,
      deviceTypeName: deviceTypeName
    };
    return this.http.post<ChartTableViewDataModel>(`${this.apiUrl}/device/getChartTableView`, payload);
  }

  getNorrelChartTableViewData(deviceId: string, startDate: Date, endDate: Date, offset: number, limit: number, view: string = 'day', deviceTypeName: string): Observable<ChartTableViewDataModel> {
    const payload: ChartTableViewModel = {
      deviceId: deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      offset: offset,
      limit: limit,
      view: view,
      deviceTypeName: deviceTypeName
    };
    return this.http.post<ChartTableViewDataModel>(`${this.apiUrl}/devicemanagementview/chartTableView`, payload);
  }

  /**
   * Converts a local date to location timezone and then to UTC
   * The date components (year, month, day, hour, etc.) are interpreted as being in the location's timezone
   * @param date - The date with components in local time
   * @param timezoneId - The IANA timezone identifier (e.g., 'America/New_York')
   * @returns Date object in UTC
   */
  convertToLocationTimezone(date: Date, timezoneId: string): Date {
    if (!timezoneId) {
      return date; // Return as-is if no timezone specified
    }

    try {
      // Get the date components from the input date as numbers
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const milliseconds = date.getMilliseconds();

      // Create ISO string interpreting these values as location timezone
      const monthStr = String(month).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const hoursStr = String(hours).padStart(2, '0');
      const minutesStr = String(minutes).padStart(2, '0');
      const secondsStr = String(seconds).padStart(2, '0');
      const millisecondsStr = String(milliseconds).padStart(3, '0');
      const dateString = `${year}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:${secondsStr}.${millisecondsStr}`;

      // Convert to UTC by creating a date and adjusting for timezone
      // Use Intl.DateTimeFormat to get the offset
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneId,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      // Create the date assuming it's in the location timezone
      const locationDate = new Date(dateString);

      // Get what this date would be in UTC if it were in the location timezone
      const parts = formatter.formatToParts(locationDate);
      const locationYear = parseInt(parts.find(p => p.type === 'year')?.value || '0');
      const locationMonth = parseInt(parts.find(p => p.type === 'month')?.value || '0');
      const locationDay = parseInt(parts.find(p => p.type === 'day')?.value || '0');
      const locationHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
      const locationMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
      const locationSecond = parseInt(parts.find(p => p.type === 'second')?.value || '0');

      // Calculate the offset between what we want and what we got
      const wantedDate = new Date(Date.UTC(year, date.getMonth(), day, hours, minutes, seconds, milliseconds));
      const gotDate = new Date(Date.UTC(locationYear, locationMonth - 1, locationDay, locationHour, locationMinute, locationSecond));

      const offset = wantedDate.getTime() - gotDate.getTime();

      // Apply the offset to get the correct UTC time
      return new Date(locationDate.getTime() + offset);
    } catch (error) {
      console.error('Error converting to location timezone:', error);
      return date; // Return original date if conversion fails
    }
  }


  // Get eGauge Energy Chart Data
  getEGaugeEnergyChart(buildingId: string, timezoneId: string): Observable<any[]> {
    const payload: EGaugeChartDataModel = {
      buildingId: buildingId,
      MemberTimezone: timezoneId
    };
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<any[]>(
      `${this.apiUrl}/egaugedevice/geteGaugeEnergyChart`,
      payload,
      { headers }
    );
  }

  // Get lighting zone details for a device
  getLightingZones(deviceId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/device/${deviceId}/lighting/zone`);
  }

  // Save/update lighting zone details for a device
  saveLightingZones(deviceId: string, zones: any[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/device/${deviceId}/lighting/zone`, { zones });
  }
}


